import { getConfig } from '@/config/config.ts';
import type { InstrumentWithFailures } from '@/store/instruments/model/Instrument.ts';
import type { AlternativeInstrument } from '@/store/instruments/model/payloads/AlternativeInstrument.ts';
import type { BondInstrument } from '@/store/instruments/model/payloads/BondInstrument.ts';
import type { EquityInstrument } from '@/store/instruments/model/payloads/EquityInstrument.ts';
import type { ForwardInstrument } from '@/store/instruments/model/payloads/ForwardInstrument.ts';
import type { OtherInstrument } from '@/store/instruments/model/payloads/OtherInstrument.ts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { WebApiResponseWithFailures } from './WebApiResponse.ts';
import { webApiResponseBaseQuery } from './webApiResponseBaseQuery.ts';
import type { LegalEntity } from '@/model/LegalEntity.ts';

const prefixUrl = getConfig().prefixUrl ?? '';

export const instrumentApi = createApi({
  reducerPath: 'instrumentApi',
  keepUnusedDataFor: 3_600, // By defaut keep query data 1h even if unused
  baseQuery: webApiResponseBaseQuery,
  endpoints: (builder) => ({
    getEquityInstrument: builder.query<InstrumentWithFailures<EquityInstrument>, {instrumentCode: string, entity: LegalEntity}>({
      query: ({instrumentCode, entity}) => ({
        method: 'GET',
        url: `${prefixUrl}/instruments/equities/${instrumentCode}`,
        params: {
          legalEntity: entity
        },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<EquityInstrument>) => ({
        instrument: response.data[0],
        failures: response.failures ?? [],
      }),
    }),

    getBondInstrument: builder.query<InstrumentWithFailures<BondInstrument>, {instrumentCode: string, entity: LegalEntity}>({
      query: ({instrumentCode, entity}) => ({
        method: 'GET',
        url: `${prefixUrl}/instruments/bonds/${instrumentCode}`,
        params: {
          legalEntity: entity
        },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<BondInstrument>) => ({
        instrument: response.data[0],
        failures: response.failures ?? [],
      }),
    }),

    getAlternativeInstrument: builder.query<InstrumentWithFailures<AlternativeInstrument>, {instrumentCode: string, entity: LegalEntity}>({
      query: ({instrumentCode, entity}) => ({
        method: 'GET',
        url: `${prefixUrl}/instruments/alternatives/${instrumentCode}`,
        params: {
          legalEntity: entity
        },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<AlternativeInstrument>) => ({
        instrument: response.data[0],
        failures: response.failures ?? [],
      }),
    }),

    getForwardInstrument: builder.query<InstrumentWithFailures<ForwardInstrument>, {instrumentCode: string, entity: LegalEntity}>({
      query: ({instrumentCode, entity}) => ({
        method: 'GET',
        url: `${prefixUrl}/instruments/forwards/${instrumentCode}`,
        params: {
          legalEntity: entity
        },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<ForwardInstrument>) => ({
        instrument: response.data[0],
        failures: response.failures ?? [],
      }),
    }),

    getOtherInstrument: builder.query<InstrumentWithFailures<OtherInstrument>, {instrumentCode: string, entity: LegalEntity}>({
      query: ({instrumentCode, entity}) => ({
        method: 'GET',
        url: `${prefixUrl}/instruments/others/${instrumentCode}`,
        params: {
          legalEntity: entity
        },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<OtherInstrument>) => ({
        instrument: response.data[0],
        failures: response.failures ?? [],
      }),
    }),
  }),
});

export const {
  useLazyGetEquityInstrumentQuery,
  useLazyGetBondInstrumentQuery,
  useLazyGetAlternativeInstrumentQuery,
  useLazyGetForwardInstrumentQuery,
  useLazyGetOtherInstrumentQuery,
} = instrumentApi;
